import { Component, OnInit, Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';

import { GTMService } from './services/gtm.service';
import { selectAllVouchers, selectVouchersLoading } from './store/selectors/voucher.selectors';
import { selectMgmConfigLoading, selectVouchersProvider } from './store/selectors/mgm.selectors';
import { selectInvitesLoading, selectTotalInvites } from './store/selectors/invite.selectors';
import { getToken, getUser } from './store/selectors/auth.selectors';
import { Voucher } from 'src/app/interfaces/voucher';
import * as StartActions from 'src/app/store/actions/start.actions';
import { isLoading } from 'src/app/store/selectors/start.selectors';
import { CookieService } from 'ngx-cookie-service';
import * as AuthActions from './store/actions/auth.actions';
import { AuthService } from './services/auth.service';
import { environment } from '../environments/environment';
import { applyAccessRedirect, normalizeRoutePath } from './utils/access-redirect';

@Component({
  selector: 'app-root',
  template: `
    <overlay-loading *ngIf="startLoading$ | async"></overlay-loading>

    <main *ngIf="!(startLoading$ | async)">
      <acceptance-terms-modal *ngIf="user"></acceptance-terms-modal>
      <voucher-alert-modal
        [vouchers]="vouchers$ | async"
        [inviteAccepts]="totalInvites$ |async"
        [voucherProvider]="voucherProvider$ | async"
        *ngIf="canShowVoucherModal && !(vouchersLoading$ | async) && !(mgmConfigLoading$ | async) && !(invitesLoading$ | async)"
      ></voucher-alert-modal>
      <toaster></toaster>
      <establishment-redirection></establishment-redirection>
      <router-outlet></router-outlet>
      <platform-access-blocked-modal></platform-access-blocked-modal>
    </main>
  `
})
export class AppComponent implements OnInit {

  vouchers$: Observable<Voucher[]>;
  voucherProvider$: Observable<any>;
  totalInvites$: Observable<number>;
  vouchersLoading$: Observable<boolean>;
  mgmConfigLoading$: Observable<boolean>;
  invitesLoading$: Observable<boolean>;
  startLoading$: Observable<boolean>;

  user;
  token: string;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private gtm: GTMService,
    private store: Store,
    private cookieService: CookieService,
    private authService: AuthService,
    private renderer: Renderer2,
  ) {
    const languageCurrent = navigator.language;

    translate.addLangs(['en', 'pt-br']);
    translate.setDefaultLang('en');
    languageCurrent === 'pt-BR' || languageCurrent === 'pt' ? translate.use('pt-br') : translate.use('en');

    router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        gtm.setPageView();

        if (!this.user) {
          sessionStorage.setItem('trialToaster', 'true');
        }

        if (this.user) {
          applyAccessRedirect(this.user, normalizeRoutePath(event.urlAfterRedirects));
        }
      });
  }

  ngOnInit() {
    this.addGTMScript();
    this.vouchers$ = this.store.pipe(select(selectAllVouchers));
    this.voucherProvider$ = this.store.pipe(select(selectVouchersProvider));
    this.vouchersLoading$ = this.store.pipe(select(selectVouchersLoading));
    this.invitesLoading$ = this.store.pipe(select(selectInvitesLoading));
    this.mgmConfigLoading$ = this.store.pipe(select(selectMgmConfigLoading));
    this.totalInvites$ = this.store.pipe(select(selectTotalInvites));
    this.startLoading$ = this.store.pipe(select(isLoading));

    const cookieToken = this.cookieService.get(environment.COOKIE_AT);
    const businessId = this.cookieService.get(environment.COOKIE_BID);
    const businessType = this.cookieService.get(environment.COOKIE_BT);

    this.store.pipe(select(getUser)).subscribe(user => this.user = user);
    this.store.pipe(select(getToken)).subscribe(token => this.token = token);

    if (cookieToken !== this.token) {
      this.store.dispatch(AuthActions.clear());
    }

    if (cookieToken || !this.token) {
      this.store.dispatch(StartActions.init({ token: cookieToken || this.token, businessId, businessType  }));
    } else if (!cookieToken) {
      this.store.dispatch(AuthActions.clear());
      this.authService.logout();
    }
  }

  private addGTMScript() {
    const iframe = this.renderer.createElement('iframe');
    this.renderer.setAttribute(iframe, 'src', `https://www.googletagmanager.com/ns.html?id=${environment.GTM_ID}`);
    this.renderer.setStyle(iframe, 'display', 'none');
    this.renderer.setStyle(iframe, 'visibility', 'hidden');
    this.renderer.setAttribute(iframe, 'height', '0');
    this.renderer.setAttribute(iframe, 'width', '0');

    const noscript = this.renderer.createElement('noscript');
    this.renderer.appendChild(noscript, iframe);

    this.renderer.appendChild(document.body, noscript);
  }

  get canShowVoucherModal() {
    return this.user && this.user.typeBusiness !== 'establishment';
  }
}
